import { makeLogger } from '@/utils/logger/logger.ts';
import { formatUUIDForLog } from '@/utils/logger/utils.ts';
import { TASK_STEPS } from '@/context/house/tasks/utils.ts';
import { TOTAL_TASK_STEPS } from '@/context/house/tasks/types.ts';

const HOUSE_LOGGER_NAME = 'HouseTaskManager';
export const logger = makeLogger(HOUSE_LOGGER_NAME);

/**
 * Formats and emits a task-scoped log line with a consistent prefix and step counter.
 * Example: [task-id: <uuid>] [step X/N] message
 */
export const logTask = (
	uiTaskId: string,
	step: (typeof TASK_STEPS)[number] | number | null,
	level: 'info' | 'warn' | 'error',
	message: string,
) => {
	const stepIndex = typeof step === 'number' ? step : step ? TASK_STEPS.indexOf(step) + 1 : null;
	const parts = [
		`[task-id: ${formatUUIDForLog(uiTaskId)}]`,
		stepIndex ? `[step ${stepIndex}/${TOTAL_TASK_STEPS}]` : null,
		message,
	];
	const output = parts.filter(Boolean).join(' ');
	logger[level](output);
};

// Small helpers to reduce string building in HTMC
export const logBatchProgress = (taskId: string, batchId: string, completed: number, total: number) => {
	logTask(taskId, TOTAL_TASK_STEPS, 'info', `Task completed. Batch ${batchId}: ${completed}/${total} completed`);
};

export const logBatchFailureProgress = (taskId: string, batchId: string, failed: number, completed: number) => {
	logTask(
		taskId,
		TOTAL_TASK_STEPS,
		'warn',
		`Task failed. Batch ${batchId}: ${failed} failed, ${completed} completed`,
	);
};

export const logBatchCompleted = (batchId: string, completed: number, failed: number, skipped = 0, cancelled = 0) => {
	logger.info(
		`Batch ${batchId} completed. ${completed} successful, ${failed} failed, ${skipped} skipped, ${cancelled} cancelled`,
	);
};

export const logRetryScheduled = (taskId: string, retryIndex: number, heldUpForMs: number, delay: number) => {
	logTask(taskId, null, 'info', `Scheduling retry ${retryIndex} in ${delay}ms (held up ${heldUpForMs}ms so far)`);
};
